import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { AuthContext } from '../../contexts/AuthContext';
import manualService from '../../services/manualService';
import { canAccess, getApp } from '../../manual';

export const POLL_INTERVAL_MS = 2000;
export const POLL_TIMEOUT_MS = 120000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * ManualAsk
 *
 * "Ask the manual" on the Help page. The question is answered from the whole
 * manual by the help assistant (an async job, polled like the eMoney Ask box),
 * with links to the apps the answer relies on. People rate the answer; the
 * ratings, and questions the manual couldn't answer, go into a log the
 * release-notes drafter reads to improve the manual.
 *
 * index: the loaded manual index. appId (optional): the Help page this box is
 * on, sent as context. compact: a smaller box for an app's page.
 */
const ManualAsk = ({ index, appId = null, compact = false }) => {
  const auth = useContext(AuthContext);
  const [question, setQuestion] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Bumped on every new question and on unmount: a poll loop that sees a
  // newer run id stops without touching state.
  const runIdRef = useRef(0);

  useEffect(() => () => { runIdRef.current += 1; }, []);

  const ask = async (event) => {
    event.preventDefault();
    const text = question.trim();
    if (text.length < 3 || running) return;

    runIdRef.current += 1;
    const runId = runIdRef.current;
    const isCurrent = () => runIdRef.current === runId;
    setRunning(true);
    setError('');
    setResult(null);

    try {
      const { job_id: jobId } = await manualService.ask(text, appId);
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (isCurrent()) {
        await sleep(POLL_INTERVAL_MS);
        if (!isCurrent()) return;
        const job = await manualService.getAnswer(jobId);
        if (!isCurrent()) return;
        if (job.status === 'completed') {
          setResult({ ...job, jobId });
          break;
        }
        if (job.status === 'failed') {
          setError(job.error || "The help assistant couldn't answer just now. Try again in a minute.");
          break;
        }
        if (Date.now() > deadline) {
          setError('That took too long. Try again, or ask a shorter question.');
          break;
        }
      }
    } catch (err) {
      console.error('Error asking the manual:', err);
      if (isCurrent()) setError("Your question couldn't be sent. Try again.");
    } finally {
      if (isCurrent()) setRunning(false);
    }
  };

  const placeholder = compact
    ? 'Ask a question about this app…'
    : 'Ask a question, e.g. "How do I check a client\'s cash reserve?"';

  return (
    <div className={`card ${compact ? 'mb-3' : 'border-primary mb-4'}`} data-testid="manual-ask">
      <div className="card-body">
        {!compact && (
          <h2 className="h6 mb-2">
            <i className="bi bi-chat-dots me-2"></i>
            Ask the manual
          </h2>
        )}
        <form className="d-flex gap-2" onSubmit={ask}>
          <input
            type="text"
            className="form-control"
            placeholder={placeholder}
            value={question}
            maxLength={1000}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={running}
            aria-label="Question for the manual"
          />
          <button type="submit" className="btn btn-primary text-nowrap" disabled={running || question.trim().length < 3}>
            {running ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Thinking…
              </>
            ) : (
              'Ask'
            )}
          </button>
        </form>

        {error && (
          <div className="alert alert-warning py-2 mt-3 mb-0" role="alert">
            {error}
          </div>
        )}

        {result && <Answer result={result} index={index} auth={auth} />}
      </div>
    </div>
  );
};

const Answer = ({ result, index, auth }) => {
  const apps = (result.app_ids || []).map((id) => getApp(index, id)).filter(Boolean);
  return (
    <div className="mt-3" data-testid="manual-answer">
      <div className="manual-content">
        <ReactMarkdown skipHtml>{result.answer || ''}</ReactMarkdown>
      </div>
      {result.answered === false && (
        <div className="small text-muted mt-2">
          <i className="bi bi-info-circle me-1"></i>
          The manual doesn't cover this yet. Your question has been logged so it can be added.
        </div>
      )}
      {apps.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          {apps.map((app) => (
            <div key={app.id} className="border rounded px-2 py-1 d-flex align-items-center gap-2 small">
              <i className={`bi ${app.icon}`}></i>
              <Link to={`/help/${app.id}`}>{app.title}</Link>
              {app.route && canAccess(app, auth) && (
                <Link to={app.route} className="btn btn-sm btn-outline-primary py-0">
                  Open
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
      <Feedback jobId={result.jobId} />
    </div>
  );
};

const Feedback = ({ jobId }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [state, setState] = useState('idle'); // idle | asking-why | sending | sent | failed

  const send = async (value, text = '') => {
    setState('sending');
    try {
      await manualService.sendFeedback(jobId, value, text);
      setState('sent');
    } catch (err) {
      console.error('Error sending feedback:', err);
      setState('failed');
    }
  };

  const rate = (value) => {
    setRating(value);
    if (value === 'up') {
      send('up');
    } else {
      setState('asking-why');
    }
  };

  if (state === 'sent') {
    return <div className="small text-success mt-3">Thanks, this helps improve the manual.</div>;
  }

  return (
    <div className="mt-3">
      <div className="d-flex align-items-center gap-2 small">
        <span className="text-muted">Was this helpful?</span>
        <button
          type="button"
          className={`btn btn-sm ${rating === 'up' ? 'btn-success' : 'btn-outline-secondary'}`}
          onClick={() => rate('up')}
          disabled={state === 'sending'}
          aria-label="Helpful"
        >
          <i className="bi bi-hand-thumbs-up"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${rating === 'down' ? 'btn-danger' : 'btn-outline-secondary'}`}
          onClick={() => rate('down')}
          disabled={state === 'sending'}
          aria-label="Not helpful"
        >
          <i className="bi bi-hand-thumbs-down"></i>
        </button>
      </div>
      {state === 'asking-why' && (
        <form
          className="mt-2"
          onSubmit={(e) => {
            e.preventDefault();
            send('down', comment.trim());
          }}
        >
          <textarea
            className="form-control form-control-sm"
            rows={2}
            maxLength={1000}
            placeholder="What was missing or wrong? (optional)"
            aria-label="What was missing or wrong"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button type="submit" className="btn btn-sm btn-primary mt-2">
            Send
          </button>
        </form>
      )}
      {state === 'failed' && (
        <div className="small text-danger mt-2">Your feedback couldn't be sent. Try again.</div>
      )}
    </div>
  );
};

export default ManualAsk;
